import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import i18n from '@/i18n/config';
import { sanitizeDbError } from '@/lib/sanitizeError';
import type { Tables } from '@/integrations/supabase/types';
import {
  persistedF2FScheduleResult,
  resolveF2FScheduleUiOutcome,
} from '@/lib/forkhire/workflow/f2fDialogMutation';

export type Candidate = Tables<'candidates'>;
export type Application = Tables<'applications'>;
export type CvScore = Tables<'cv_scores'>;

type AdvanceStageBody = {
  application_id: string;
  target_stage: string;
  expected_source_stage: string;
  target_status?: string;
};

type StageBlockDetails = {
  error?: string;
  reason?: string;
  stage?: string;
  idempotent?: boolean;
  partial_success?: boolean;
  schedule_persisted?: boolean;
  notification_status?: string;
  notification_error?: string | null;
  notification_retry?: boolean;
  notification_status_persisted?: boolean;
  notification_outcome_recorded?: boolean;
  downstream_finalized?: boolean;
  delivery_summary?: { complete?: boolean } | null;
  [key: string]: unknown;
};

type StageAdvanceErrorWithContext = {
  context?: {
    json?: () => Promise<StageBlockDetails>;
  };
};

type ForkHireCandidateApplicationsRpcClient = {
  rpc: (
    fn: 'get_forkhire_candidate_applications',
    params: {
      _candidate_id: string | null;
      _job_id: string | null;
      _application_id: string | null;
      _active_candidates_only?: boolean;
    },
  ) => Promise<{ data: unknown; error: unknown }>;
};

type SeekApplicationStatusRow = {
  hire_application_id: string | null;
  application_status_candidate_view: string | null;
  source_channel?: string | null;
};

type CandidateApplicationWithRelations = Application & {
  is_withdrawn?: boolean;
  details_unlocked?: boolean;
  contact_visibility?: 'protected' | 'unavailable' | 'visible' | null;
  contact_source?: 'cv_extracted' | 'candidate_submitted' | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  contact_location?: string | null;
  cv_scores: CvScore | null;
  jobs: {
    title: string;
    department: string | null;
    status: string;
    location: string | null;
    employment_type: string | null;
    created_at: string;
    job_number: number | null;
  } | null;
  interview_sessions: {
    id: string;
    overall_score: number | null;
    status: string;
    expires_at?: string | null;
  }[];
};

type CandidatePreview = Omit<CandidateWithApplication, 'applications'>;
type ApplicationWithCandidatePreview = CandidateApplicationWithRelations & {
  candidates: CandidatePreview;
  is_reapplied?: boolean;
};

export const APPLICATION_STAGE_REFRESH_QUERY_KEYS = [
  ['candidates'],
  ['job-candidates'],
  ['job-app-counts'],
  ['dashboard'],
  ['hire-analytics'],
  ['f2f-schedules'],
  ['employer-notifications'],
  ['seek-notifications'],
] as const;

export interface CandidateWithApplication extends Candidate {
  applications: CandidateApplicationWithRelations[];
}

async function fetchForkHireCandidateApplications(args: {
  candidateId?: string | null;
  jobId?: string | null;
  applicationId?: string | null;
  activeCandidatesOnly?: boolean;
} = {}) {
  const { data, error } = await (supabase as unknown as ForkHireCandidateApplicationsRpcClient).rpc('get_forkhire_candidate_applications', {
    _candidate_id: args.candidateId ?? null,
    _job_id: args.jobId ?? null,
    _application_id: args.applicationId ?? null,
    _active_candidates_only: args.activeCandidatesOnly ?? false,
  });

  if (error) throw error;
  const rows = (Array.isArray(data) ? data : []) as CandidateWithApplication[];
  const applicationIds = rows
    .flatMap((candidate) => candidate.applications ?? [])
    .map((application) => application.id)
    .filter(Boolean);

  if (applicationIds.length === 0) return rows;

  const { data: sessionExpiryRows, error: sessionExpiryError } = await supabase
    .from('interview_sessions')
    .select('id, application_id, expires_at')
    .in('application_id', applicationIds);

  if (sessionExpiryError) throw sessionExpiryError;
  const expiryBySessionId = new Map(
    (sessionExpiryRows ?? []).map((session) => [session.id, session.expires_at])
  );

  return rows.map((candidate) => ({
    ...candidate,
    applications: (candidate.applications ?? []).map((application) => ({
      ...application,
      interview_sessions: (application.interview_sessions ?? []).map((session) => ({
        ...session,
        expires_at: expiryBySessionId.get(session.id) ?? null,
      })),
    })),
  }));
}

async function mergeSeekWithdrawnStatus(rows: CandidateWithApplication[]) {
  const applicationIds = rows
    .flatMap((candidate) => candidate.applications ?? [])
    .map((app) => app.id)
    .filter(Boolean);

  if (applicationIds.length === 0) return rows;

  const { data: seekApps } = await supabase
    .from('seek_applications')
    .select('hire_application_id, application_status_candidate_view')
    .in('hire_application_id', applicationIds);

  const withdrawnApplicationIds = new Set(
    ((seekApps ?? []) as SeekApplicationStatusRow[])
      .filter((sa) => sa.application_status_candidate_view === 'withdrawn' && sa.hire_application_id)
      .map((sa) => sa.hire_application_id as string)
  );

  if (withdrawnApplicationIds.size === 0) return rows;

  return rows.map((candidate) => ({
    ...candidate,
    applications: (candidate.applications ?? []).map((app) =>
      withdrawnApplicationIds.has(app.id)
        ? { ...app, is_withdrawn: true, status: 'withdrawn' }
        : app
    ),
  }));
}

export function useCandidates() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['candidates', orgId],
    queryFn: async () => {
      // The unscoped review feed includes accepted Applied/AI Reviewed rows.
      // Protected contact/CV fields remain redacted by the authoritative RPC
      // until the existing shortlist permission boundary is crossed.
      const rows = await fetchForkHireCandidateApplications({ activeCandidatesOnly: false });
      return await mergeSeekWithdrawnStatus(rows);
    },
    enabled: !!orgId,
  });
}

export function useCandidate(id: string | undefined, applicationId?: string | null) {
  return useQuery({
    queryKey: ['candidates', id, applicationId ?? null],
    queryFn: async () => {
      const rows = await fetchForkHireCandidateApplications({ candidateId: id, applicationId });
      const [candidate] = await mergeSeekWithdrawnStatus(rows);
      return candidate;
    },
    enabled: !!id,
  });
}

export function useJobCandidates(jobId: string | undefined) {
  return useQuery({
    queryKey: ['job-candidates', jobId],
    queryFn: async () => {
      const candidateRows = await fetchForkHireCandidateApplications({ jobId });
      const data = candidateRows.flatMap((candidate) => {
        const { applications, ...candidatePreview } = candidate;
        return (applications ?? []).map((app): ApplicationWithCandidatePreview => ({
          ...app,
          candidates: candidatePreview,
        }));
      });

      // Check for withdrawn/reapplied seek_applications linked to these ForkHire applications
      if (data && data.length > 0) {
        const appIds = data.map((a) => a.id);
        const { data: seekApps } = await supabase
          .from('seek_applications')
          .select('hire_application_id, application_status_candidate_view, source_channel')
          .in('hire_application_id', appIds);
        
        const withdrawnMap = new Map<string, boolean>();
        const reappliedMap = new Map<string, boolean>();
        ((seekApps ?? []) as SeekApplicationStatusRow[]).forEach((sa) => {
          if (sa.application_status_candidate_view === 'withdrawn' && sa.hire_application_id) {
            withdrawnMap.set(sa.hire_application_id, true);
          }
          if (sa.source_channel === 'seek_ai_reapply' && sa.hire_application_id) {
            reappliedMap.set(sa.hire_application_id, true);
          }
        });

        // Also check cv_parsed_json.reapplied flag
        return data.map((app) => ({
          ...app,
          is_withdrawn: withdrawnMap.get(app.id) ?? false,
          is_reapplied: reappliedMap.get(app.id) ?? false,
        }));
      }

      return data;
    },
    enabled: !!jobId,
  });
}

export function useUpdateApplicationStage() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ applicationId, stage, expectedSourceStage, status, secondInterviewDetails }: {
      applicationId: string; stage: string; expectedSourceStage: string; status?: string;
      secondInterviewDetails?: { interview_type: string; scheduled_date: string; scheduled_time: string; meeting_link?: string; location_link?: string; attendee_emails?: string[] };
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Unauthorized');
      }

      if (stage === 'second_interview') {
        if (!secondInterviewDetails) {
          const body: AdvanceStageBody = {
            application_id: applicationId,
            target_stage: stage,
            expected_source_stage: expectedSourceStage,
          };
          const res = await supabase.functions.invoke('advance-stage', {
            body,
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          });
          if (res.error) {
            const context = typeof res.error === 'object' && res.error !== null && 'context' in res.error
              ? (res.error as StageAdvanceErrorWithContext).context
              : undefined;
            if (context && typeof context.json === 'function') {
              try {
                const details = await context.json() as StageBlockDetails;
                if (details?.error) {
                  const reason = details.reason ? ` (${details.reason})` : '';
                  throw new Error(`${details.error}${reason}`);
                }
              } catch (error) {
                if (error instanceof Error && error.message !== 'Unexpected end of JSON input') throw error;
              }
            }
            const msg = typeof res.error === 'object' && 'message' in res.error
              ? res.error.message : String(res.error);
            throw new Error(msg || 'F2F stage movement failed');
          }
          return {
            ...(res.data ?? {}),
            mutation_kind: 'stage_only_f2f',
          };
        }

        const res = await supabase.functions.invoke('f2f-schedule', {
          body: {
            action: 'schedule',
            application_id: applicationId,
            expected_source_stage: expectedSourceStage,
            details: secondInterviewDetails,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        if (res.error) {
          const context = typeof res.error === 'object' && res.error !== null && 'context' in res.error
            ? (res.error as StageAdvanceErrorWithContext).context
            : undefined;
          if (context && typeof context.json === 'function') {
            let details: StageBlockDetails | null = null;
            try {
              details = await context.json();
            } catch {
              // Fall back to the Supabase function error message below.
            }
            const persistedResult = persistedF2FScheduleResult(details);
            if (persistedResult) {
              return {
                ...persistedResult,
                stage: persistedResult.stage ?? 'second_interview',
                mutation_kind: 'f2f_schedule',
              };
            }
            if (details?.error) {
              const reason = details.reason ? ` (${details.reason})` : '';
              throw new Error(`${details.error}${reason}`);
            }
          }
          const msg = typeof res.error === 'object' && 'message' in res.error
            ? res.error.message : String(res.error);
          throw new Error(msg || 'F2F scheduling failed');
        }
        return {
          ...(res.data ?? {}),
          mutation_kind: 'f2f_schedule',
        };
      }

      // Route non-F2F stage transitions through the advance-stage edge function
      // for consistent side-effects (seek sync, notifications, session creation, emails)
      const body: AdvanceStageBody = {
        application_id: applicationId,
        target_stage: stage,
        expected_source_stage: expectedSourceStage,
      };
      if (status) body.target_status = status;
      const res = await supabase.functions.invoke('advance-stage', {
        body,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (res.error) {
        let blockDetails: StageBlockDetails | null = null;
        const context = typeof res.error === 'object' && res.error !== null && 'context' in res.error
          ? (res.error as StageAdvanceErrorWithContext).context
          : undefined;
        if (context && typeof context.json === 'function') {
          try {
            blockDetails = await context.json();
          } catch {
            blockDetails = null;
          }
        }
        if (blockDetails?.error) {
          const reason = blockDetails.reason ? ` (${blockDetails.reason})` : '';
          throw new Error(`${blockDetails.error}${reason}`);
        }
        const msg = typeof res.error === 'object' && 'message' in res.error
          ? res.error.message : String(res.error);
        throw new Error(msg || 'Stage advance failed');
      }
      return res.data;
    },
    onSuccess: (data) => {
      APPLICATION_STAGE_REFRESH_QUERY_KEYS.forEach((queryKey) => {
        qc.invalidateQueries({ queryKey });
      });
      const sideEffects = data?.side_effects || [];
      const extras = sideEffects.length > 0 
        ? ` (${sideEffects.map((s: string) => s.replace(/_/g, ' ')).join(', ')})` 
        : '';
      if (data?.stage === 'second_interview') {
        if (data.mutation_kind === 'stage_only_f2f') {
          toast({ title: i18n.t('hire.lateStage.success.move_to_f2f') });
          return;
        }
        const outcome = resolveF2FScheduleUiOutcome(data);
        const copy = {
          notification_delivery_failed: {
            title: 'hire.f2fSchedule.savedNotificationFailed',
            description: data.notification_error || i18n.t('hire.f2fSchedule.retryNotification'),
          },
          notification_status_reconciliation_incomplete: {
            title: 'hire.f2fSchedule.savedStatusReconciliationIncomplete',
            description: i18n.t('hire.f2fSchedule.statusReconciliationReview'),
          },
          downstream_reconciliation_incomplete: {
            title: 'hire.f2fSchedule.savedDownstreamIncomplete',
            description: i18n.t('hire.f2fSchedule.downstreamReconciliationReview'),
          },
          persisted_incomplete: {
            title: 'hire.f2fSchedule.savedReconciliationIncomplete',
            description: i18n.t('hire.f2fSchedule.reconciliationReview'),
          },
          already_saved: {
            title: 'hire.f2fSchedule.alreadySaved',
            description: null,
          },
          success: {
            title: 'hire.f2fSchedule.savedEmailSent',
            description: null,
          },
        }[outcome];
        toast({
          title: i18n.t(copy.title),
          description: copy.description || undefined,
        });
        return;
      }
      toast({ title: `Application updated${extras}` });
    },
    onError: (e) => {
      toast({ title: 'Update failed', description: sanitizeDbError(e), variant: 'destructive' });
    },
  });
}
